import React, { useState } from "react";
import { Button, Alert, Typography, Link, Stack, Paper, Box, Tab, AlertTitle } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Divider from "@mui/material/Divider";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import WarningIcon from '@mui/icons-material/Warning';
import { useFileDownload } from "../Hooks/useFileDownload";

const RenderDownloadButton = (filename, version = 'File', link = false) => {
    const { downloading, progress, downloadByFilenames, error } = useFileDownload(null);

    const handleDownload = () => {
        downloadByFilenames([filename], (index, status) => {});
    };

    return (
        <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Button
                variant="text"
                size="small"
                sx={{ 
                    justifyContent: "flex-start",
                    textTransform: "none",
                    py: 0.5,
                    px: 0,
                    minWidth: "auto",
                    '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline'
                    }
                }}
                onClick={handleDownload}
                disabled={downloading}
                aria-label={`Download ${version}`}
            >
                <DownloadIcon sx={{ mr: 1, fontSize: 18 }} />
                {downloading ? `Downloading (${progress}%)` : version}
            </Button>
        </>
    );
};

const NonSDLComponent = ({ activeTheme, infoCardStyle }) => {
    const [toolValue, setToolValue] = useState("1");

    const handleToolChange = (event, newValue) => {
        setToolValue(newValue);
    };

    return (
        <Box sx={{ px: 4, py: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                NON-SDL Tools
            </Typography>
            
            {/* Deprecation Warning */}
            <Alert 
                severity="warning" 
                icon={<WarningIcon fontSize="large" />}
                sx={{ 
                    mb: 3,
                    '& .MuiAlert-message': {
                        width: '100%'
                    }
                }}
            >
                <AlertTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                    Deprecation Notice
                </AlertTitle>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    This page will be deprecated soon and will no longer be maintained.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Please make alternative arrangements and download any necessary tools before this page is removed.
                </Typography>
            </Alert>
            
            <TabContext value={toolValue}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <TabList 
                        onChange={handleToolChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            "& .MuiTab-root": {
                                textTransform: "none",
                                fontWeight: 500,
                                minHeight: "auto",
                                py: 1.5,
                            },
                        }}
                    >
                        <Tab label="VVT" value="1"/>
                        <Tab label="GM SOMEIP Simulator" value="2"/>
                    </TabList>
                </Box>

                {/* VVT Tool */}
                <TabPanel value="1" sx={{ px: 0 }}>
                    <Paper elevation={0} sx={infoCardStyle}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Documentation
                        </Typography>
                        <Link href="#" target="_blank" rel="noopener noreferrer" underline="hover">
                            [NOT PROVIDED]
                        </Link>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Developer
                        </Typography>
                        <Typography variant="body2">
                            Matthew Rytlewski (matthew.4.rytlewski@gm.com)
                        </Typography>
                    </Paper>
                    
                    <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                        Available Versions
                    </Typography>
                    <Stack spacing={0.5} sx={{ pl: 1 }}>
                        {RenderDownloadButton('home/tools/VVT/VVT_2.0.3.zip', 'Version 2.0.3 (2025-10-23)', true)}
                        {RenderDownloadButton('home/tools/VVT/Pre_ODO_update_release_3.zip', 'Pre ODO update release 3 (2025-10-23)', true)}
                        {RenderDownloadButton('home/tools/VVT/VVT_2.0.1.zip', 'Version 2.0.1 (2025-7-23)', true)}
                        {RenderDownloadButton('home/tools/VVT/VVT_2.0.0.zip', 'Version 2.0.0 (2025-6-3)', true)}
                    </Stack>
                </TabPanel>

                {/* GM SOMEIP Simulator */}
                <TabPanel value="2" sx={{ px: 0 }}>
                    <Paper elevation={0} sx={infoCardStyle}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Documentation
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Link href="https://gm-sdv.atlassian.net/wiki/spaces/IE/pages/625806244/GM+SOMEIP+Simulator" target="_blank" rel="noopener noreferrer" underline="hover">
                                Confluence
                            </Link>
                            <Link href="https://github.com/GM-InnerSource/CCU-Simulator" target="_blank" rel="noopener noreferrer" underline="hover">
                                GitHub Readme
                            </Link>
                        </Stack>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Developer
                        </Typography>
                        <Typography variant="body2">
                            Amit Pai (amit.1.pai@gm.com)
                        </Typography>
                    </Paper>
                    
                    <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                        Available Versions
                    </Typography>
                    <Stack spacing={0.5} sx={{ pl: 1 }}>
                        {RenderDownloadButton('home/tools/GM_SOMEIP_Simulator/Setup_GM_SOMEIP_Simulator_V2.0.12.zip', 'Version 2.0.12 (2025-11-17)', true)} 
                        {RenderDownloadButton('home/tools/GM_SOMEIP_Simulator/Setup_GM_SOMEIP_Simulator_V2.0.8.zip', 'Version 2.0.8 (2025-9-18)', true)}
                        {RenderDownloadButton('home/tools/GM_SOMEIP_Simulator/Setup_GM_SOMEIP_Simulator_V2.0.3.zip', 'Version 2.0.3 (2025-7-24)', true)}
                        {RenderDownloadButton('home/tools/GM_SOMEIP_Simulator/Setup_GM_SOMEIP_Simulator_V2.0.0.zip', 'Version 2.0.0 (2025-7-11)', true)}
                        {RenderDownloadButton('home/tools/GM_SOMEIP_Simulator/Setup_GM_SOMEIP_Simulator_V1.10.0.zip', 'Version 1.10.0 (2025-6-10)', true)}
                        {RenderDownloadButton('home/tools/GM_SOMEIP_Simulator/Setup_GM_SOMEIP_Simulator_V1.7.0.zip', 'Version 1.7.0 (2025-5-30)', true)}
                    </Stack>
                </TabPanel>
            </TabContext>
        </Box>
    );
};

export default NonSDLComponent;
